import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft, Users, Search, X, Building2, Sparkles } from 'lucide-react';
import { jwtDecode } from "jwt-decode";
import AlertBox from '../../Components/AlertBox';

const EditProject = ({ projects, setProjects, onNavigate, projectId }) => {
  const navigate = useNavigate();
  const { id } = useParams();

  // Use projectId prop if provided, otherwise use id from useParams
  const effectiveProjectId = projectId || id;
  const [isLoaded, setIsLoaded] = useState(false);
  const [showAlert, setShowAlert] = useState(null);
  const [shouldNavigate, setShouldNavigate] = useState(false);

  const handleAlertClose = () => {
    setShowAlert(null);
    if (shouldNavigate) {
      if (onNavigate) {
        onNavigate('/projects', 'Projects');
      } else {
        navigate('/projects');
      }
      setShouldNavigate(false);
    }
  };
  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Fetch specific project data from API
  const [projectData, setProjectData] = useState(null);

  useEffect(() => {
    const fetchProjectData = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`https://slateblue-mallard-430836.hostingersite.com/api/projectsmgnt/${effectiveProjectId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const project = await response.json();
          console.log('EditProject - Fetched project from API:', project);
          setProjectData(project);
        } else {
          console.error('EditProject - Failed to fetch project:', response.status);
        }
      } catch (error) {
        console.error('EditProject - Error fetching project:', error);
      }
    };

    if (effectiveProjectId) {
      fetchProjectData();
    }
  }, [effectiveProjectId]);

  // ---------------- Simple Styles ----------------
  const editProjectStyle = {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    padding: '2rem',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
  };

  const pageHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
    padding: '1.5rem',
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  };

  const headerTitleStyle = {
    fontSize: '1.875rem',
    fontWeight: 700,
    color: '#1f2937',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  };

  const btnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.5rem 1rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    fontWeight: 500,
    cursor: 'pointer',
    gap: '0.5rem',
    transition: 'all 0.2s ease',
    fontSize: '0.875rem',
    backgroundColor: 'white'
  };

  const btnSecondaryStyle = {
    ...btnStyle,
    backgroundColor: '#6b7280',
    color: 'white',
    borderColor: '#6b7280'
  };

  const btnPrimaryStyle = {
    ...btnStyle,
    backgroundColor: '#3b82f6',
    color: 'white',
    borderColor: '#3b82f6'
  };

  const formContainerStyle = {
    maxWidth: '800px',
    margin: '0 auto',
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    padding: '2rem',
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  };
  const formGroupStyle = { marginBottom: '2rem', marginRight: '1.5rem' };
  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 500,
    color: '#374151',
    fontSize: '0.875rem'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: 'white',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    outline: 'none',
    color: '#1f2937',
    transition: 'border-color 0.2s ease'
  };

  const textareaStyle = { ...inputStyle, resize: 'vertical', minHeight: '100px' };
  const selectStyle = { ...inputStyle, cursor: 'pointer' };
  const formRowStyle = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' };
  const formActionsStyle = { display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '2rem' };

  const employeeSearchBoxStyle = {
    display: 'flex',
    alignItems: 'center',
    border: '1px solid #d1d5db',
    borderRadius: '0.5rem',
    padding: '0.5rem',
    gap: '0.5rem',
    backgroundColor: '#f9fafb',
  };

  const employeeListStyle = {
    maxHeight: '150px',
    overflowY: 'auto',
    border: '1px solid #d1d5db',
    borderRadius: '0.5rem',
    padding: '0.5rem',
    background: '#fff',
    marginTop: '0.5rem',
  };

  const selectedEmployeesContainer = { display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' };
  const selectedEmployeeChip = {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#f3f4f6',
    border: '1px solid #d1d5db',
    borderRadius: '0.5rem',
    padding: '0.5rem 0.75rem',
    fontSize: '0.85rem',
    color: '#1f2937',
    margin: '0.25rem'
  };

  // ---------------- States ----------------
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [departmentSearchTerm, setDepartmentSearchTerm] = useState('');
  const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [newCustomField, setNewCustomField] = useState({ key: '', value: '' });
  // Status management - using API
  const [statusOptions, setStatusOptions] = useState([]);
  const [newStatus, setNewStatus] = useState('');
  const [showStatusForm, setShowStatusForm] = useState(false);

  // Function to fetch custom statuses from API
  const fetchCustomStatuses = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('https://slateblue-mallard-430836.hostingersite.com/api/prjCustomStatus/', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const statuses = await response.json();
        console.log('EditProject - API Response:', statuses);
        console.log('EditProject - Response type:', typeof statuses);
        console.log('EditProject - Is array:', Array.isArray(statuses));

        // Handle wrapped response format {statuses: Array}
        let statusArray = [];
        if (Array.isArray(statuses)) {
          statusArray = statuses;
        } else if (statuses && statuses.statuses && Array.isArray(statuses.statuses)) {
          console.log('EditProject - Found wrapped statuses array');
          statusArray = statuses.statuses;
        } else {
          console.log('EditProject - Invalid response format:', statuses);
          setStatusOptions([]);
          return;
        }

        if (statusArray.length > 0) {
          console.log('EditProject - First status object:', statusArray[0]);
          console.log('EditProject - First status keys:', Object.keys(statusArray[0]));

          // Extract status names from the array
          const statusNames = statusArray.map(status => {
            console.log('EditProject - Processing status:', status);
            // Try to get the name field
            if (status && typeof status === 'object' && status.name) {
              console.log('EditProject - Found name:', status.name);
              return status.name;
            }
            console.log('EditProject - No name field found');
            return null;
          }).filter(name => name); // Remove null values

          console.log('EditProject - Extracted status names:', statusNames);
          setStatusOptions(statusNames);
        } else {
          console.log('EditProject - Empty status array');
          setStatusOptions([]);
        }
      } else {
        console.error('EditProject - API Error:', response.status);
        setStatusOptions([]);
      }
    } catch (error) {
      console.error('EditProject - Fetch Error:', error);
      setStatusOptions([]);
    }
  };

  // Fetch custom statuses on component mount
  useEffect(() => {
    fetchCustomStatuses();
  }, []);

  // Monitor statusOptions changes
  useEffect(() => {
    console.log('EditProject - statusOptions updated:', statusOptions);
    console.log('EditProject - statusOptions length:', statusOptions.length);
  }, [statusOptions]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: '',
    startDate: '',
    dueDate: '',
    budget: '',
    progress: 0,
    departments: [], // Changed to array for multiple departments
    clientName: '',
    clientCompany: '',
    clientEmail: '',
    clientMobileNo: '',
    clientAddress: '',
    clientCity: '',
    clientState: '',
    projectHead: '',
    selectedEmployeeId: '',
    customFields: [] // Array of {key, value} objects
  });

  // Get token from localStorage
  const token = localStorage.getItem('authToken');

  let COMPANY_ID = '';
  if (token) {
    try {
      const decodedToken = jwtDecode(token); // Decode JWT
      // Handle both company and employee login types
      COMPANY_ID = decodedToken.role === 'company'
        ? (decodedToken.companyId || decodedToken.id)
        : decodedToken.companyId;
      console.log("Decoded Token:", decodedToken);
      console.log("Extracted Company ID:", COMPANY_ID);
      console.log("User Role:", decodedToken.role);
    } catch (err) {
      console.error("Error decoding token:", err);
    }
  }

  // ---------------- Fetch Employees ----------------
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const token = localStorage.getItem('authToken'); // or wherever you store the token

        console.log("EditProject - Fetching employees for company:", COMPANY_ID);

        const res = await fetch(`https://slateblue-mallard-430836.hostingersite.com/api/employees/company/${COMPANY_ID}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (res.ok) {
          const data = await res.json();
          console.log("EditProject - Employees fetched:", data?.length, "employees");
          if (data && data.length > 0) {
            console.log("EditProject - Sample employee:", data[0]);
          }
          setEmployees(data ? data.filter(emp => emp.isActive !== false) : []);
        } else {
          console.error('EditProject - Failed to fetch employees - Status:', res.status);

          // Try multiple fallback endpoints for company login
          let fallbackData = null;

          // Try general employees endpoint with auth
          try {
            const fallbackRes = await fetch('https://slateblue-mallard-430836.hostingersite.com/api/employees', {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            });
            if (fallbackRes.ok) {
              fallbackData = await fallbackRes.json();
              console.log("EditProject - Auth fallback employees fetched:", fallbackData?.length);
            }
          } catch (error) {
            console.log("EditProject - Auth fallback failed, trying no-auth");
          }

          // Try no-auth fallback for company access
          if (!fallbackData) {
            try {
              const noAuthRes = await fetch('https://slateblue-mallard-430836.hostingersite.com/api/employees');
              if (noAuthRes.ok) {
                fallbackData = await noAuthRes.json();
                console.log("EditProject - No-auth fallback employees fetched:", fallbackData?.length);
              }
            } catch (error) {
              console.log("EditProject - No-auth fallback failed");
            }
          }

          setEmployees(fallbackData || []);
        }
      } catch (error) {
        console.error('Error fetching employees:', error);
      }
    };
    fetchEmployees();
  }, []);

  // ---------------- Fetch Departments ----------------
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        // Get token from localStorage
        const token = localStorage.getItem('authToken');

        // Use the already extracted COMPANY_ID from above
        console.log("EditProject - Using Company ID for departments:", COMPANY_ID);

        let departmentData = null;

        // First try: with company ID and auth
        try {
          const response = await fetch(`https://slateblue-mallard-430836.hostingersite.com/api/departments/company/${COMPANY_ID}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            departmentData = await response.json();
            console.log('EditProject - Departments fetched with auth:', departmentData?.length);
          }
        } catch (error) {
          console.log("EditProject - Auth department fetch failed, trying alternatives");
        }

        // Second try: without auth for company access
        if (!departmentData) {
          try {
            const response = await fetch(`https://slateblue-mallard-430836.hostingersite.com/api/departments/company/${COMPANY_ID}`);
            if (response.ok) {
              departmentData = await response.json();
              console.log('EditProject - Departments fetched without auth:', departmentData?.length);
            }
          } catch (error) {
            console.log("EditProject - No-auth department fetch failed");
          }
        }

        // Third try: general departments endpoint
        if (!departmentData) {
          try {
            const response = await fetch('https://slateblue-mallard-430836.hostingersite.com/api/departments', {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            if (response.ok) {
              departmentData = await response.json();
              console.log('EditProject - General departments fetched:', departmentData?.length);
            }
          } catch (error) {
            console.log("EditProject - General department fetch failed");
          }
        }

        console.log('EditProject - Final departments data:', departmentData);
        const finalDepartments = Array.isArray(departmentData) ? departmentData : [];
        console.log('EditProject - Setting departments:', finalDepartments);
        setDepartments(finalDepartments);
      } catch (error) {
        console.error('EditProject - Error fetching departments:', error);
        setDepartments([]);
      }
    };

    fetchDepartments();
  }, []);

  // Debug useEffect to monitor departments loading
  useEffect(() => {
    console.log('EditProject - Departments updated:', departments);
    console.log('EditProject - Departments count:', departments.length);
    if (departments.length > 0) {
      console.log('EditProject - Sample department:', departments[0]);
    }
  }, [departments]);

  // ---------------- Click Outside Handler ----------------
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDepartmentDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // ---------------- Validation Helpers ----------------
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidPhone = (phone) => /^[6-9]\d{9}$/.test(phone);

  // ---------------- Computed Values ----------------
  // Filter departments based on search term
  const filteredDepartments = departments.filter(dept =>
    dept.name.toLowerCase().includes(departmentSearchTerm.toLowerCase())
  );

  // Group employees by department
  const employeesByDepartment = employees.reduce((acc, emp) => {
    const deptId = emp.department?._id || emp.department || 'no-department';
    const deptName = emp.department?.name || 'No Department';

    if (!acc[deptId]) {
      acc[deptId] = {
        id: deptId,
        name: deptName,
        employees: []
      };
    }
    acc[deptId].employees.push(emp);
    return acc;
  }, {});

  // Filter employees based on selected departments
  const filteredEmployees = employees.filter(emp => {
    if (!formData.departments || formData.departments.length === 0) return false; // Show none if no departments selected
    return formData.departments.some(deptId =>
      String(emp.department) === String(deptId) ||
      String(emp.department?._id) === String(deptId)
    );
  });

  // Get department-wise filtered employees for display
  const departmentWiseEmployees = formData.departments.reduce((acc, deptId) => {
    const deptEmployees = employees.filter(emp =>
      String(emp.department) === String(deptId) ||
      String(emp.department?._id) === String(deptId)
    );

    if (deptEmployees.length > 0) {
      const dept = departments.find(d => d._id === deptId);
      acc[deptId] = {
        name: dept?.name || 'Unknown Department',
        employees: deptEmployees
      };
    }
    return acc;
  }, {});

  // ---------------- Load Project Data ----------------
  useEffect(() => {
    // Use fetched projectData first, then fall back to projects prop
    const project = projectData || projects.find((p) => p._id === effectiveProjectId);
    if (project && departments.length > 0) {
      console.log("EditProject - Loading project data:", project);
      console.log("EditProject - Project departments:", project.departments);
      console.log("EditProject - Available departments:", departments);

      // Handle both 'departments' array and legacy 'department' field
      let projectDepartments = [];

      if (project.departments && Array.isArray(project.departments)) {
        // New format: departments array
        projectDepartments = project.departments;
      } else if (project.department) {
        // Legacy format: single department field
        if (Array.isArray(project.department)) {
          projectDepartments = project.department;
        } else {
          // Single department (could be ID or object)
          projectDepartments = [project.department?._id || project.department];
        }
      }

      console.log("EditProject - Raw project.departments:", project.departments);
      console.log("EditProject - Raw project.department:", project.department);
      console.log("EditProject - Processed departments:", projectDepartments);
      console.log("EditProject - Available departments for lookup:", departments);

      // Note: Don't override API-fetched statuses with project-specific ones
      // The API statuses are already loaded via fetchCustomStatuses()
      console.log("EditProject - Project status:", project.status);

      // If project has a status that's not in the current statusOptions, add it
      if (project.status && project.status.trim()) {
        setStatusOptions(prevOptions => {
          if (!prevOptions.includes(project.status)) {
            console.log("EditProject - Adding project status to existing options:", project.status);
            return [...prevOptions, project.status];
          }
          return prevOptions;
        });
      }

      // Always update formData when project or departments change, but preserve user modifications
      setFormData(prevFormData => {
        // Check if this is the initial load or if departments have changed
        const isInitialLoad = !prevFormData.title;
        const departmentsChanged = JSON.stringify(prevFormData.departments) !== JSON.stringify(projectDepartments);

        if (isInitialLoad || departmentsChanged) {
          console.log("EditProject - Updating form data with departments:", projectDepartments);
          return {
            company: project.company || '',
            departments: projectDepartments,
            title: project.title || '',
            description: project.description || '',
            status: project.status || '',
            startDate: project.startDate ? project.startDate.split('T')[0] : '',
            dueDate: project.dueDate ? project.dueDate.split('T')[0] : '',
            budget: project.budget || '',
            progress: project.progress || 0,
            clientName: project.clientName || '',
            clientCompany: project.clientCompany || '',
            clientEmail: project.clientEmail || '',
            clientMobileNo: project.clientMobileNo || '',
            clientAddress: project.clientAddress || '',
            clientCity: project.clientCity || '',
            clientState: project.clientState || '',
            projectHead: project.projectHead?._id || project.projectHead || '',
            selectedEmployeeId: '',
            customFields: project.customFields || []
          };
        }

        // If not initial load and departments haven't changed, preserve current form data
        return prevFormData;
      });

      // Pre-load team members if they exist and employees are loaded
      if (project.teamMembers && project.teamMembers.length > 0 && employees.length > 0) {
        // Map team members to ensure we have proper employee data
        const mappedTeamMembers = project.teamMembers.map((member) => {
          if (typeof member === 'string') {
            // If member is just an ID, find the full employee data
            const employee = employees.find(emp => String(emp._id) === String(member));
            return employee || { _id: member, name: 'Unknown Employee', email: '' };
          } else if (typeof member === 'object' && member !== null) {
            // If member is already an object, ensure it has proper data
            if (!member.name && member._id) {
              const employee = employees.find(emp => String(emp._id) === String(member._id));
              return employee || member;
            }
            return member;
          }
          return member;
        });

        // Only update team members if they're different to prevent clearing user selections
        setSelectedTeamMembers(prevMembers => {
          const currentIds = prevMembers.map(m => m._id).sort();
          const newIds = mappedTeamMembers.map(m => m._id).sort();

          if (JSON.stringify(currentIds) !== JSON.stringify(newIds)) {
            return mappedTeamMembers;
          }
          return prevMembers;
        });
      }
    }
  }, [effectiveProjectId, projects, employees, departments, projectData]);


  // ---------------- Handlers ----------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDepartmentToggle = (departmentId) => {
    setFormData(prev => {
      const isSelected = prev.departments.includes(departmentId);
      const newDepartments = isSelected
        ? prev.departments.filter(id => id !== departmentId)
        : [...prev.departments, departmentId];

      return {
        ...prev,
        departments: newDepartments,
        selectedEmployeeId: '' // Reset employee selection when departments change
      };
    });

    // Only remove team members if a department is being removed (not when adding)
    const isRemoving = formData.departments.includes(departmentId);
    if (isRemoving) {
      // Remove team members that belong to the department being removed
      setSelectedTeamMembers(prev =>
        prev.filter(member => {
          const memberDeptId = member.department?._id || member.department;
          return String(memberDeptId) !== String(departmentId);
        })
      );
    }
  };

  const handleAddTeamMember = () => {
    const selectedEmployee = employees.find(emp => emp._id === formData.selectedEmployeeId);
    if (selectedEmployee && !selectedTeamMembers.some(member => member._id === selectedEmployee._id)) {
      setSelectedTeamMembers(prev => [...prev, selectedEmployee]);
    }
    setFormData(prev => ({ ...prev, selectedEmployeeId: '' })); // reset dropdown
  };

  const handleRemoveTeamMember = (id) => {
    setSelectedTeamMembers((prev) => prev.filter((member) => member._id !== id));
  };

  // Custom Fields Handlers
  const handleAddCustomField = () => {
    if (newCustomField.key.trim() && newCustomField.value.trim()) {
      setFormData(prev => ({
        ...prev,
        customFields: [...prev.customFields, { ...newCustomField }]
      }));
      setNewCustomField({ key: '', value: '' });
    }
  };

  const handleRemoveCustomField = (index) => {
    setFormData(prev => ({
      ...prev,
      customFields: prev.customFields.filter((_, i) => i !== index)
    }));
  };

  const handleCustomFieldChange = (field, value) => {
    setNewCustomField(prev => ({ ...prev, [field]: value }));
  };

  // Add single status to the dropdown list using API
  const handleAddStatus = async () => {
    const label = (newStatus || '').trim();
    if (!label) return;

    const exists = statusOptions.some(s => s.toLowerCase() === label.toLowerCase());
    if (exists) {
      setShowAlert('This status already exists in your local list!');
      setNewStatus('');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('https://slateblue-mallard-430836.hostingersite.com/api/prjCustomStatus/', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          company: COMPANY_ID,
          name: label
        })
      });

      if (response.ok) {
        const newStatus = await response.json();
        console.log('EditProject - Created new status:', newStatus);

        // Refresh the status list from server to ensure we have latest data
        console.log('EditProject - Refreshing status list after creation...');
        await fetchCustomStatuses();

        // Set the new status as selected
        setFormData(prev => ({ ...prev, status: label }));

        setNewStatus('');
        // Keep modal open so user can add more statuses
        // setShowStatusForm(false);
        setShowAlert(`Status "${label}" added successfully! You can add more statuses or close the modal.`);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('EditProject - Failed to create status:', errorData);

        // Handle specific error messages from server
        if (errorData.message && errorData.message.includes('unique')) {
          setShowAlert(`Status "${label}" already exists for your company. Please choose a different name.`);
        } else {
          setShowAlert(`Failed to create status: ${errorData.message || 'Please try again.'}`);
        }

        setNewStatus('');
      }
    } catch (error) {
      console.error('EditProject - Error creating status:', error);
      setShowAlert('Error creating status. Please check your connection and try again.');
      setNewStatus('');
    }
  };

  // Open status form
  const handleOpenStatusForm = () => {
    setShowStatusForm(true);
  };

  // Close status form
  const handleCloseStatusForm = () => {
    setShowStatusForm(false);
    setNewStatus('');
  };

  // Remove a status from the list
  const handleRemoveStatus = (statusToRemove) => {
    const updated = statusOptions.filter(status => status !== statusToRemove);
    setStatusOptions(updated);
    // Note: Status options are project-specific and not saved globally

    // If the removed status was selected, change to the first remaining status or empty
    if (formData.status === statusToRemove) {
      setFormData(prev => ({ ...prev, status: updated.length > 0 ? updated[0] : '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.startDate || !formData.dueDate || formData.departments.length === 0) {
      setError('Please fill in all required fields (Title, Start Date, Due Date, Departments)');
      return;
    }
    if (statusOptions.length === 0 || !formData.status) {
      setError('Please add at least one status and select it for the project.');
      return;
    }

    // Ensure the selected status exists in statusOptions
    if (!statusOptions.includes(formData.status)) {
      console.log('EditProject - Selected status not in options, adding it:', formData.status);
      setStatusOptions(prev => [...prev, formData.status]);
    }
    if (formData.clientEmail && !isValidEmail(formData.clientEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (formData.clientMobileNo && !isValidPhone(formData.clientMobileNo)) {
      setError('Please enter a valid 10-digit mobile number.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const updatedProjectData = {
        company: formData.company || COMPANY_ID,
        department: formData.departments, // Send as 'department' field to match backend expectation
        title: formData.title,
        description: formData.description,
        status: formData.status,
        startDate: formData.startDate,
        dueDate: formData.dueDate,
        budget: formData.budget ? parseFloat(formData.budget) : 0,
        progress: parseInt(formData.progress) || 0,
        teamMembers: selectedTeamMembers.map((emp) => emp._id),
        projectHead: formData.projectHead || null,
        clientName: formData.clientName,
        clientCompany: formData.clientCompany,
        clientEmail: formData.clientEmail,
        clientMobileNo: formData.clientMobileNo,
        clientAddress: formData.clientAddress,
        clientCity: formData.clientCity,
        clientState: formData.clientState,
        customFields: formData.customFields,
        statusOptions: statusOptions, // Save updated custom statuses
      };

      console.log('EditProject - Submitting project data:', updatedProjectData);
      console.log('EditProject - Departments being sent:', formData.departments);

      const token = localStorage.getItem('authToken');

      const response = await fetch(`https://slateblue-mallard-430836.hostingersite.com/api/projectsmgnt/${effectiveProjectId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updatedProjectData),
      });

      console.log('EditProject - Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('EditProject - Update failed:', errorData);
        throw new Error(errorData.message || 'Failed to update project');
      }

      const updatedProject = await response.json();
      console.log('EditProject - Updated project received:', updatedProject);
      console.log('EditProject - Updated project departments:', updatedProject.departments);

      setProjects((prev) =>
        prev.map((project) => (project._id === effectiveProjectId ? updatedProject : project))
      );

      // Trigger refresh of ProjectList and ProjectTracker components
      console.log('EditProject - Triggering refresh, window.refreshProjects exists:', !!window.refreshProjects);
      if (window.refreshProjects) {
        await window.refreshProjects();
        console.log('EditProject - Refresh completed');
      }

      // Also refresh ProjectTracker if available
      if (window.refreshProjectTracker) {
        await window.refreshProjectTracker();
        console.log('EditProject - ProjectTracker refresh completed');
      }


      setShowAlert('✅ Project updated successfully!');
      setShouldNavigate(true);
    } catch (error) {
      console.error('Error updating project:', error);
      setError('Failed to update project. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ---------------- UI ----------------
  return (
    <div style={editProjectStyle}>
      <div style={pageHeaderStyle}>
        <button onClick={() => onNavigate ? onNavigate('/projects', 'Projects') : navigate('/projects')} style={btnSecondaryStyle}>
          <ArrowLeft size={20} />
          Back to Projects
        </button>
        <h1 style={headerTitleStyle}>
          <Sparkles size={32} style={{ color: '#f093fb' }} />
          Edit Project
        </h1>
      </div>

      <div style={formContainerStyle}>
        {error && (
          <div
            style={{
              padding: '1.5rem',
              background: 'rgba(239, 68, 68, 0.2)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '1rem',
              color: 'white',
              marginBottom: '2rem',
              boxShadow: '0 10px 25px rgba(239, 68, 68, 0.2)',
              textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)'
            }}
          >
            ⚠️ {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Title */}
          <div style={formGroupStyle}>
            <label htmlFor="title" style={labelStyle}>
              📋 Project Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Enter project title"
              required
              style={inputStyle}
            />
          </div>

          {/* Departments - Searchable Dropdown with Multiple Selection */}
          <div style={formGroupStyle}>
            <label style={labelStyle}>
              Departments *
              {formData.departments.length > 0 && (
                <span style={{
                  fontSize: '0.75rem',
                  color: '#6b7280',
                  fontWeight: 'normal',
                  marginLeft: '0.5rem',
                  padding: '0.25rem 0.5rem',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '0.375rem',
                  border: '1px solid #d1d5db'
                }}>
                  {formData.departments.length} selected
                </span>
              )}
            </label>

            {/* Search Input with Dropdown Toggle */}
            <div style={{ position: 'relative' }} ref={dropdownRef}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="Search departments..."
                  value={departmentSearchTerm}
                  onChange={(e) => setDepartmentSearchTerm(e.target.value)}
                  onFocus={() => setShowDepartmentDropdown(true)}
                  style={{
                    ...inputStyle,
                    paddingRight: '2.5rem'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowDepartmentDropdown(!showDepartmentDropdown)}
                  style={{
                    position: 'absolute',
                    right: '0.5rem',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.25rem',
                    color: '#6b7280',
                    fontSize: '1rem'
                  }}
                >
                  {showDepartmentDropdown ? '–' : '–'}
                </button>
              </div>

              {/* Dropdown List */}
              {showDepartmentDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000,
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  {filteredDepartments.length > 0 ? (
                    filteredDepartments.map((dept) => (
                      <div
                        key={dept._id}
                        onClick={() => handleDepartmentToggle(dept._id)}
                        style={{
                          padding: '0.75rem',
                          cursor: 'pointer',
                          backgroundColor: formData.departments.includes(dept._id) ? '#f0f9ff' : 'white',
                          borderBottom: '1px solid #f3f4f6',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (!formData.departments.includes(dept._id)) {
                            e.target.style.backgroundColor = '#f9fafb';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!formData.departments.includes(dept._id)) {
                            e.target.style.backgroundColor = 'white';
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.departments.includes(dept._id)}
                          onChange={() => { }}
                          style={{ cursor: 'pointer' }}
                        />
                        <span style={{
                          fontSize: '0.875rem',
                          color: formData.departments.includes(dept._id) ? '#0c4a6e' : '#374151',
                          fontWeight: formData.departments.includes(dept._id) ? '500' : 'normal'
                        }}>
                          {dept.name}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div style={{
                      padding: '0.75rem',
                      textAlign: 'center',
                      color: '#6b7280',
                      fontSize: '0.875rem'
                    }}>
                      {departmentSearchTerm ? 'No departments found' : 'No departments available'}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Selected Departments Display */}
            {formData.departments && formData.departments.length > 0 && (
              <div style={{ marginTop: '0.75rem' }}>
                <div style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '0.75rem', fontWeight: '500' }}>
                  Selected Departments ({formData.departments.length}):
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                  {formData.departments.map((deptId, index) => {
                    console.log(`EditProject - Rendering department ${index + 1}:`, deptId);
                    console.log(`EditProject - Available departments:`, departments);

                    // Enhanced department lookup - handle both ID strings and objects
                    let dept = null;
                    let departmentName = 'Unknown Department';

                    // If deptId is already an object with name
                    if (typeof deptId === 'object' && deptId !== null && deptId.name) {
                      dept = deptId;
                      departmentName = deptId.name;
                    } else {
                      // Try to find department by ID
                      const searchId = String(deptId);

                      // First try direct ID match
                      dept = departments.find(d => String(d._id) === searchId);

                      // If not found, try other ID fields
                      if (!dept) {
                        dept = departments.find(d => String(d.id) === searchId);
                      }

                      // If not found, try departmentId field
                      if (!dept) {
                        dept = departments.find(d => String(d.departmentId) === searchId);
                      }

                      // If still not found, try name match (in case deptId is actually a name)
                      if (!dept && typeof deptId === 'string') {
                        dept = departments.find(d => d.name === deptId || d.departmentName === deptId);
                      }

                      // Set department name
                      if (dept) {
                        departmentName = dept.name || dept.departmentName || dept.title || 'Unknown Department';
                      } else {
                        // If still not found, show the ID for debugging
                        departmentName = `Dept ID: ${deptId}`;
                      }
                    }

                    console.log(`EditProject - Found department:`, dept);
                    console.log(`EditProject - Department name:`, departmentName);

                    return (
                      <span key={deptId} style={{
                        fontSize: '0.875rem',
                        padding: '0.5rem 0.75rem',
                        backgroundColor: dept ? '#e0f2fe' : '#fef3c7',
                        color: dept ? '#0c4a6e' : '#92400e',
                        borderRadius: '0.5rem',
                        border: dept ? '1px solid #bae6fd' : '1px solid #fcd34d',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.2s ease',
                        fontStyle: dept ? 'normal' : 'italic'
                      }}>
                        <span style={{ fontWeight: '500' }}>{departmentName}</span>
                        <button
                          type="button"
                          onClick={() => handleDepartmentToggle(deptId)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: dept ? '#0c4a6e' : '#92400e',
                            cursor: 'pointer',
                            padding: '0.25rem',
                            fontSize: '0.875rem',
                            fontWeight: 'bold',
                            borderRadius: '0.25rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '20px',
                            height: '20px',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = dept ? 'rgba(12, 74, 110, 0.1)' : 'rgba(146, 64, 14, 0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                          }}
                          title="Remove department"
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Team Members - Dropdown */}
          <div style={formGroupStyle}>
            {(() => {
              // Debug logging
              console.log('EditProject - Department filtering debug:', {
                selectedDepartments: formData.departments,
                totalEmployees: employees.length,
                employeeSample: employees.slice(0, 2).map(emp => ({
                  name: emp.name || emp.teamMemberName,
                  department: emp.department,
                  departmentId: emp.department?._id || emp.department
                }))
              });

              // Filter employees based on selected departments
              const filteredEmployees = employees.filter(emp => {
                if (formData.departments.length === 0) return false;

                // Handle different department formats
                let empDeptIds = [];

                if (emp.department) {
                  if (Array.isArray(emp.department)) {
                    // Employee department is an array of department IDs
                    empDeptIds = emp.department.map(dept => {
                      if (typeof dept === 'object') {
                        return dept._id || dept.id;
                      }
                      return dept;
                    }).filter(Boolean);
                  } else if (typeof emp.department === 'object') {
                    // Employee department is a single object
                    const deptId = emp.department._id || emp.department.id;
                    if (deptId) empDeptIds = [deptId];
                  } else if (typeof emp.department === 'string') {
                    // Employee department is a single string ID
                    empDeptIds = [emp.department];
                  }
                }

                // Check if any of employee's departments match any selected department
                const matches = empDeptIds.length > 0 && empDeptIds.some(empDeptId =>
                  formData.departments.some(selectedDeptId =>
                    String(empDeptId) === String(selectedDeptId)
                  )
                );

                if (matches) {
                  console.log('✅ Employee matched:', {
                    employee: emp.name || emp.teamMemberName,
                    empDeptIds: empDeptIds,
                    department: emp.department,
                    matchingDepts: empDeptIds.filter(empDeptId =>
                      formData.departments.some(selectedDeptId =>
                        String(empDeptId) === String(selectedDeptId)
                      )
                    )
                  });
                } else {
                  console.log('❌ Employee not matched:', {
                    employee: emp.name || emp.teamMemberName,
                    empDeptIds: empDeptIds,
                    department: emp.department,
                    selectedDepts: formData.departments
                  });
                }

                return matches;
              });

              console.log('Department filtering result:', {
                selectedDepartments: formData.departments,
                totalEmployees: employees.length,
                filteredEmployees: filteredEmployees.length,
                employeeNames: filteredEmployees.map(emp => emp.name || emp.teamMemberName)
              });

              console.log('Filtered employees result:', {
                filteredCount: filteredEmployees.length,
                filteredNames: filteredEmployees.map(emp => emp.name || emp.teamMemberName)
              });

              // Group employees by department for better display
              const departmentWiseEmployees = {};
              filteredEmployees.forEach(emp => {
                // Handle employees with multiple departments
                if (Array.isArray(emp.department)) {
                  emp.department.forEach(dept => {
                    let deptId, deptName;

                    if (typeof dept === 'object') {
                      deptId = dept._id || dept.id;
                      deptName = dept.name || dept.departmentName;
                    } else {
                      deptId = dept;
                      // Find department name from departments array
                      const foundDept = departments.find(d => String(d._id) === String(dept));
                      deptName = foundDept?.name || foundDept?.departmentName || `Department ${dept}`;
                    }

                    // Only group if this department is selected
                    if (formData.departments.includes(deptId)) {
                      if (!departmentWiseEmployees[deptId]) {
                        departmentWiseEmployees[deptId] = {
                          name: deptName,
                          employees: []
                        };
                      }

                      // Avoid duplicate employees in same department
                      if (!departmentWiseEmployees[deptId].employees.find(e => e._id === emp._id)) {
                        departmentWiseEmployees[deptId].employees.push(emp);
                      }
                    }
                  });
                } else {
                  // Handle single department
                  let deptId, deptName;

                  if (typeof emp.department === 'object') {
                    deptId = emp.department._id || emp.department.id;
                    deptName = emp.department.name || emp.department.departmentName;
                  } else {
                    deptId = emp.department;
                    const foundDept = departments.find(d => String(d._id) === String(emp.department));
                    deptName = foundDept?.name || foundDept?.departmentName || `Department ${emp.department}`;
                  }

                  if (!departmentWiseEmployees[deptId]) {
                    departmentWiseEmployees[deptId] = {
                      name: deptName,
                      employees: []
                    };
                  }
                  departmentWiseEmployees[deptId].employees.push(emp);
                }
              });

              console.log('Department grouping result:', {
                departmentWiseEmployees,
                departmentNames: Object.values(departmentWiseEmployees).map(d => d.name)
              });

              return (
                <>
                  <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Users size={20} /> Assign Team Members
                    {formData.departments.length > 0 && (
                      <span style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        fontWeight: 'normal',
                        marginLeft: '0.5rem',
                        padding: '0.25rem 0.5rem',
                        backgroundColor: '#f3f4f6',
                        borderRadius: '0.375rem',
                        border: '1px solid #d1d5db'
                      }}>
                        {filteredEmployees.length} employee{filteredEmployees.length !== 1 ? 's' : ''} available
                      </span>
                    )}
                  </label>
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <select
                      value={formData.selectedEmployeeId}
                      onChange={handleChange}
                      name="selectedEmployeeId"
                      style={selectStyle}
                      disabled={formData.departments.length === 0}
                    >
                      <option value="">
                        {formData.departments.length === 0
                          ? "Select departments first"
                          : filteredEmployees.length === 0
                            ? "No employees in selected departments"
                            : "Select a team member"
                        }
                      </option>
                      {Object.keys(departmentWiseEmployees).map(deptId => {
                        const deptGroup = departmentWiseEmployees[deptId];
                        return [
                          <option key={`dept-${deptId}`} disabled style={{ fontWeight: 'bold', backgroundColor: '#f3f4f6' }}>
                            {deptGroup.name} ({deptGroup.employees.length} employees) </option>,
                          ...deptGroup.employees.map(emp => (
                            <option key={emp._id} value={emp._id} style={{ paddingLeft: '1rem' }}>
                              &nbsp;&nbsp;&nbsp;&nbsp;{emp.teamMemberName || emp.name || 'Unnamed'} - {emp.role || 'No Position'}
                            </option>
                          ))
                        ];
                      }).flat()}
                    </select>
                    <button
                      type="button"
                      onClick={handleAddTeamMember}
                      style={{
                        ...btnPrimaryStyle,
                        padding: '0.5rem 1rem',
                        opacity: formData.departments.length === 0 || filteredEmployees.length === 0 ? 0.5 : 1,
                        cursor: formData.departments.length === 0 || filteredEmployees.length === 0 ? 'not-allowed' : 'pointer'
                      }}
                      disabled={formData.departments.length === 0 || filteredEmployees.length === 0}
                    >
                      Add
                    </button>
                  </div>

                  {/* Selected Members */}
                  {selectedTeamMembers.length > 0 && (
                    <div style={selectedEmployeesContainer}>
                      {selectedTeamMembers.map((member) => (
                        <div key={member._id} style={{
                          display: 'flex',
                          alignItems: 'center',
                          background: '#f0f9ff',
                          padding: '0.75rem 1rem',
                          borderRadius: '0.5rem',
                          fontSize: '0.9rem',
                          gap: '0.75rem',
                          minHeight: 'auto',
                          border: '1px solid #bae6fd',
                          color: '#0c4a6e',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                          transition: 'all 0.2s ease'
                        }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <span style={{ fontWeight: '500' }}>
                              {member.teamMemberName || member.name || 'Unnamed'}
                            </span>
                            <span style={{ fontSize: '0.75rem', color: '#64748b', fontStyle: 'italic' }}>
                              {member.role || 'No Position'}
                            </span>
                          </div>
                          <X
                            size={14}
                            style={{ cursor: 'pointer', marginLeft: '0.5rem' }}
                            onClick={() => handleRemoveTeamMember(member._id)}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          {/* Project Head */}
          <div style={formGroupStyle}>
            <label htmlFor="projectHead" style={labelStyle}>💼 Project Head</label>
            <select
              id="projectHead"
              name="projectHead"
              value={formData.projectHead}
              onChange={handleChange}
              style={selectStyle}
            >
              <option value="">Select Project Head</option>
              {employees.map(emp => (
                <option key={emp._id} value={emp._id}>
                  {emp.teamMemberName || emp.name || 'Unnamed'} - {emp.role || 'No Position'}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div style={formGroupStyle}>
            <label htmlFor="description" style={labelStyle}>
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Project description..."
              style={textareaStyle}
            />
          </div>

          {/* Status */}
          <div style={formGroupStyle}>
            <label htmlFor="status" style={labelStyle}>
              📊 Status
            </label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              style={selectStyle}
            >
              <option value="">-- Select Status (Add statuses first) --</option>
              {statusOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            {/* Add Status Button */}
            <div style={{ marginTop: '0.75rem' }}>
              <button
                type="button"
                onClick={handleOpenStatusForm}
                style={{
                  ...btnPrimaryStyle,
                  whiteSpace: 'nowrap'
                }}
              >
                ž• Add & Manage Statuses
              </button>
            </div>

            {/* Status Form Modal */}
            {showStatusForm && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
              }}>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '1rem',
                  padding: '2rem',
                  width: '400px',
                  maxWidth: '90vw',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
                }}>
                  <h3 style={{
                    margin: '0 0 1.5rem 0',
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    color: '#1f2937',
                    textAlign: 'center'
                  }}>
                    📊 Add & Manage Statuses
                  </h3>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontWeight: 500,
                        color: '#374151',
                        fontSize: '0.875rem'
                      }}>
                        Status Name *
                      </label>
                      <input
                        type="text"
                        value={newStatus}
                        onChange={(e) => setNewStatus(e.target.value)}
                        placeholder="Enter status name (e.g., On Hold, Under Review, Testing)"
                        style={{
                          ...inputStyle,
                          marginBottom: '0.5rem'
                        }}
                        autoFocus
                      />
                    </div>

                    {/* Show current status count */}
                    <div style={{
                      padding: '0.75rem',
                      backgroundColor: '#f0f9ff',
                      borderRadius: '0.5rem',
                      border: '1px solid #bae6fd',
                      marginBottom: '0.5rem'
                    }}>
                      <div style={{
                        fontSize: '0.75rem',
                        color: '#0c4a6e',
                        fontWeight: 500,
                        marginBottom: '0.25rem'
                      }}>
                        📊 Current Status Count: {statusOptions.length}
                      </div>
                      <div style={{
                        fontSize: '0.875rem',
                        color: '#1f2937'
                      }}>
                        {newStatus.trim() ? (
                          <span>
                            Adding: <span style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#dbeafe',
                              borderRadius: '0.25rem',
                              fontWeight: '500'
                            }}>
                              {newStatus.trim()}
                            </span>
                            <span style={{ color: '#6b7280', marginLeft: '0.5rem' }}>†Total will be {statusOptions.length + 1}</span>
                          </span>
                        ) : (
                          <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Enter status name to add</span>
                        )}
                      </div>
                    </div>

                    <div style={{
                      fontSize: '0.75rem',
                      color: '#6b7280',
                      fontStyle: 'italic'
                    }}>
                      Examples: On Hold, Under Review, Testing, Cancelled, Paused, Approved
                    </div>

                    <div style={{
                      fontSize: '0.75rem',
                      color: '#059669',
                      fontWeight: '500',
                      marginTop: '0.5rem',
                      padding: '0.5rem',
                      backgroundColor: '#ecfdf5',
                      borderRadius: '0.375rem',
                      border: '1px solid #a7f3d0'
                    }}>
                      ¡ Tip: Add multiple statuses one by one. The modal will stay open until you click "Cancel".
                    </div>
                  </div>

                  {/* Existing Statuses Management */}
                  {statusOptions.length > 0 && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <div style={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: '#374151',
                        marginBottom: '0.75rem',
                        borderBottom: '1px solid #e5e7eb',
                        paddingBottom: '0.5rem'
                      }}>
                        —ï Existing Statuses ({statusOptions.length})
                      </div>

                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        padding: '0.5rem',
                        backgroundColor: '#f8fafc',
                        borderRadius: '0.5rem',
                        border: '1px solid #e5e7eb'
                      }}>
                        {statusOptions.map((status, index) => (
                          <div key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '0.5rem 0.75rem',
                            backgroundColor: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '0.375rem'
                          }}>
                            <span style={{
                              fontSize: '0.875rem',
                              fontWeight: '500',
                              color: '#1f2937'
                            }}>
                              {status}
                            </span>

                            <button
                              type="button"
                              onClick={() => {
                                if (window.confirm(`Remove "${status}"?`)) {
                                  handleRemoveStatus(status);
                                }
                              }}
                              style={{
                                backgroundColor: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.25rem',
                                padding: '0.25rem 0.5rem',
                                cursor: 'pointer',
                                fontSize: '0.75rem',
                                fontWeight: '500'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#dc2626';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#ef4444';
                              }}
                            >
                              ❌
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={{
                    display: 'flex',
                    gap: '1rem',
                    justifyContent: 'flex-end'
                  }}>
                    <button
                      type="button"
                      onClick={handleCloseStatusForm}
                      style={{
                        ...btnSecondaryStyle,
                        padding: '0.75rem 1.5rem'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleAddStatus}
                      style={{
                        ...btnPrimaryStyle,
                        padding: '0.75rem 1.5rem',
                        opacity: (newStatus || '').trim() ? 1 : 0.5,
                        cursor: (newStatus || '').trim() ? 'pointer' : 'not-allowed'
                      }}
                      disabled={!(newStatus || '').trim()}
                    >
                      ž• Add This Status
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Start & Due Dates */}
          <div style={formRowStyle}>
            <div style={formGroupStyle}>
              <label htmlFor="startDate" style={labelStyle}>
                … Start Date *
              </label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                required
                style={inputStyle}
              />
            </div>

            <div style={formGroupStyle}>
              <label htmlFor="dueDate" style={labelStyle}>
                Due Date *
              </label>
              <input
                type="date"
                id="dueDate"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleChange}
                required
                style={inputStyle}
              />
            </div>
          </div>

          {/* Budget */}
          <div style={formGroupStyle}>
            <label htmlFor="budget" style={labelStyle}>
              💰 Budget
            </label>
            <input
              type="number"
              id="budget"
              name="budget"
              value={formData.budget}
              onChange={handleChange}
              placeholder="Enter budget"
              min="0"
              step="0.01"
              style={{
                ...inputStyle,
                MozAppearance: 'textfield'
              }}
              onWheel={(e) => e.target.blur()}
            />
            <style>{`
              #budget::-webkit-outer-spin-button,
              #budget::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
              }
            `}</style>
          </div>

          {/* Progress */}
          <div style={formGroupStyle}>
            <label htmlFor="progress" style={labelStyle}>
              📈 Progress (0-100)
            </label>
            <input
              type="number"
              id="progress"
              name="progress"
              value={formData.progress}
              onChange={handleChange}
              min="0"
              max="100"
              style={inputStyle}
            />
          </div>

          {/* Client Info */}
          <div style={formGroupStyle}>
            <h3 style={{ ...labelStyle, fontSize: '1.3rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              Client Information
            </h3>
            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label htmlFor="clientName" style={labelStyle}>
                  ï Client Name
                </label>
                <input
                  type="text"
                  id="clientName"
                  name="clientName"
                  value={formData.clientName}
                  onChange={handleChange}
                  placeholder="Enter client name"
                  style={inputStyle}
                />
              </div>

              <div style={formGroupStyle}>
                <label htmlFor="clientCompany" style={labelStyle}>
                  Client Company
                </label>
                <input
                  type="text"
                  id="clientCompany"
                  name="clientCompany"
                  value={formData.clientCompany}
                  onChange={handleChange}
                  placeholder="Enter client company"
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label htmlFor="clientEmail" style={labelStyle}>
                  Client Email
                </label>
                <input
                  type="email"
                  id="clientEmail"
                  name="clientEmail"
                  value={formData.clientEmail}
                  onChange={handleChange}
                  placeholder="client@company.com"
                  style={inputStyle}
                />
              </div>

              <div style={formGroupStyle}>
                <label htmlFor="clientMobileNo" style={labelStyle}>
                  📱 Client Mobile Number
                </label>
                <input
                  type="tel"
                  id="clientMobileNo"
                  name="clientMobileNo"
                  value={formData.clientMobileNo}
                  onChange={handleChange}
                  placeholder="10-digit mobile number"
                  style={inputStyle}
                />
              </div>
            </div>

            {/* Client Address Information */}
            <div style={formGroupStyle}>
              <label htmlFor="clientAddress" style={labelStyle}>  Client Address</label>
              <textarea
                id="clientAddress"
                name="clientAddress"
                value={formData.clientAddress}
                onChange={handleChange}
                placeholder="Enter client address"
                style={{ ...textareaStyle, minHeight: '80px' }}
              />
            </div>

            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label htmlFor="clientCity" style={labelStyle}>™ï Client City</label>
                <input
                  type="text"
                  id="clientCity"
                  name="clientCity"
                  value={formData.clientCity}
                  onChange={handleChange}
                  placeholder="Enter client city"
                  style={inputStyle}
                />
              </div>

              <div style={formGroupStyle}>
                <label htmlFor="clientState" style={labelStyle}>—ï Client State</label>
                <input
                  type="text"
                  id="clientState"
                  name="clientState"
                  value={formData.clientState}
                  onChange={handleChange}
                  placeholder="Enter client state"
                  style={inputStyle}
                />
              </div>
            </div>

          </div>

          {/* Custom Fields */}
          <div style={formGroupStyle}>
            <h3 style={{ ...labelStyle, fontSize: '1.3rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              š™ï Other Information
            </h3>

            {/* Add New Custom Field */}
            <div style={{ marginBottom: '1rem' }}>
              <div style={formRowStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>ï Name</label>
                  <input
                    type="text"
                    value={newCustomField.key}
                    onChange={(e) => handleCustomFieldChange('key', e.target.value)}
                    placeholder="Enter field name"
                    style={inputStyle}
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>¡ Particular</label>
                  <input
                    type="text"
                    value={newCustomField.value}
                    onChange={(e) => handleCustomFieldChange('value', e.target.value)}
                    placeholder="Enter field value"
                    style={inputStyle}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={handleAddCustomField}
                style={{
                  ...btnPrimaryStyle,
                  marginTop: '0.5rem',
                  opacity: (!newCustomField.key.trim() || !newCustomField.value.trim()) ? 0.5 : 1,
                  cursor: (!newCustomField.key.trim() || !newCustomField.value.trim()) ? 'not-allowed' : 'pointer'
                }}
                disabled={!newCustomField.key.trim() || !newCustomField.value.trim()}
              >
                ž• Add Other Field
              </button>
            </div>

            {/* Display Custom Fields */}
            {formData.customFields.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                  Other Fields ({formData.customFields.length}):
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {formData.customFields.map((field, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.75rem',
                      backgroundColor: '#f8fafc',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.375rem',
                      gap: '1rem'
                    }}>
                      <div style={{ display: 'flex', gap: '1rem', flex: 1 }}>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Field:</span>
                          <div style={{ fontSize: '0.875rem', color: '#1f2937', fontWeight: '500' }}>{field.key}</div>
                        </div>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Value:</span>
                          <div style={{ fontSize: '0.875rem', color: '#1f2937' }}>{field.value}</div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveCustomField(index)}
                        style={{
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.25rem',
                          padding: '0.25rem 0.5rem',
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          fontWeight: '500'
                        }}
                      >
                        ✅• Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div style={formActionsStyle}>
            <button type="button" onClick={() => onNavigate ? onNavigate('/projects', 'Projects') : navigate('/projects')} style={btnSecondaryStyle}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                ...btnPrimaryStyle,
                opacity: loading ? 0.6 : 1,
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              <Save size={20} />
              {loading ? 'Updating...' : 'Update Project'}
            </button>
          </div>
        </form>
      </div>
      {
        showAlert && (
          <AlertBox
            message={showAlert}
            onClose={handleAlertClose}
          />
        )
      }
    </div >
  );
};

export default EditProject;


